import React, { Fragment } from 'react';
import { observer } from 'mobx-react';
import { Box, Flex } from 'components/Layout/Layout';
import { FormattedMessage, useIntl } from 'react-intl';
import dateFormat from 'dateformat';

import messages from '../messages';
import { ANALYSE_BUILDING_BLOCKS } from '../../../constants';

import Button from 'components/Button/Button';
import Icon from 'components/Icon/Icon';
import H3 from 'components/H3/H3';
import Input from 'components/Input/Input';

import VideoPlayer from 'components/VideoPlayer/VideoPlayer';

import { ROUTE_ANALYSE_SELECT, ROUTE_ANALYSE_VIDEO } from 'RouteList';
import Mutation from 'components/Apollo/Mutation';
import {
  MUTATION_REMOVE_ANALYSE,
  QUERY_GET_VIDEO_ANALYSES,
  MUTATION_REMOVE_VIDEO_ANALYSE,
  MUTATION_SHARE_ANALYSE,
  MUTATION_UNSHARE_ANALYSE
} from '../../../services/graphql/video-analyse-query';
import List from '../../../components/List/List';
import ListItem from '../../../components/List/ListItem';
import { useStores } from '../../../store/useStores';

const createFormattedDate = date => dateFormat(new Date(date), 'dd/mm/yyyy');
const findAnalyseScore = (surveys, id) =>
  surveys.find(
    survey =>
      survey.buildingBlockId === id &&
      survey.last === false &&
      survey.finished === true
  );

const formatBuildingBlocks = analyse => {
  let blocksStringify = '';

  analyse.buildingBlocks.forEach((id, index) => {
    const buildingBlock = findBuildingBlock(ANALYSE_BUILDING_BLOCKS, id);
    const buildingBlockScore = findAnalyseScore(analyse.surveys, id);
    let score = buildingBlockScore ? buildingBlockScore.score : '';
    score = score > 0 ? score + '/45' : '';
    if (buildingBlock) {
      blocksStringify += buildingBlock.label + ' ' + score;
      if (index !== analyse.buildingBlocks.length - 1) {
        blocksStringify += ' en ';
      }
    }
  });

  return blocksStringify;
};

const findBuildingBlock = (list, id) => {
  let result = null;
  list.forEach(data => {
    data.data.forEach(items => {
      const buildingBlock = items.items.find(item => item.id === id);
      if (buildingBlock) {
        result = buildingBlock;
      }
    });
  });
  return result;
};

function VideoAnalyseView({
  videoAnalyse,
  deleteVideoAnalyse,
  deleteAnalyse,
  showControls = true,
  onUpdateEmail,
  email
}) {
  const intl = useIntl();
  const { authStore, routing, uiState } = useStores();
  const { user, isImpersonator } = authStore;
  const { push } = routing;

  const currentUser = isImpersonator || user;
  const shareAble =
    videoAnalyse.sharedFrom && videoAnalyse.sharedFrom.id === currentUser.id;

  const videoJsOptions = {
    playbackRates: [0.5, 1, 1.5, 2],
    sources: [
      {
        src: videoAnalyse.video.s3,
        type: 'video/mp4'
      }
    ]
  };

  const onShareAnalyse = async mutation => {
    const result = await mutation({
      variables: {
        videoAnalyseId: videoAnalyse.id,
        email
      }
    });

    if (result.data && result.data.shareVideoAnalyse === true) {
      onUpdateEmail({ target: { value: '' } });
      uiState.createAlert({
        ok: 'OK',
        title: 'Analyse is gedeeld',
        message: `De analyse is succesvol gedeeld met ${email}`
      });
    } else {
      uiState.createAlert({
        ok: 'OK',
        title: 'Analyse is niet gedeeld',
        message: `Er was een probleem met het delen van de analyse met ${email}`
      });
    }
  };

  const onUnshareAnalyse = async (mutation, person) => {
    uiState.createAlert({
      title: 'Analyse niet meer delen',
      message: `Wil je de toegang tot de analyse verwijderen voor ${person.firstname} ${person.lastname}`,
      ok: 'OK',
      okHandler: async () => {
        await mutation({
          variables: {
            videoAnalyseId: videoAnalyse.id,
            email: person.email
          }
        });
      },
      cancel: 'Cancel'
    });
  };

  return (
    <Flex width={1} my={4} flexWrap="wrap" flexDirection="column">
      <Box
        width={[1, 1, 1 / 2]}
        pr={[0, 0, 2]}
        pb={[4, 4, 0]}
        style={{ height: '100%' }}
      >
        <H3 upper lighter underlined>
          {`${intl.formatMessage(
            messages.analiseOverviewUploadDate
          )} ${createFormattedDate(videoAnalyse.date_created)}`}
        </H3>
        <Box mt={3} mb={1}>
          <VideoPlayer {...videoJsOptions} />
        </Box>
        {showControls && (
          <Flex width={1} justifyContent="space-between">
            <Button
              primary
              small
              onClick={() => push(`${ROUTE_ANALYSE_SELECT}/${videoAnalyse.id}`)}
            >
              <FormattedMessage {...messages.analiseOverviewButton} />
            </Button>
            {(user.admin ||
              videoAnalyse.sharedFrom === null ||
              videoAnalyse.sharedFrom.id === currentUser.id) && (
              <Mutation
                mutation={MUTATION_REMOVE_VIDEO_ANALYSE}
                refetchQueries={[
                  {
                    query: QUERY_GET_VIDEO_ANALYSES,
                    variables: {
                      personId: isImpersonator ? isImpersonator.id : user.id
                    }
                  }
                ]}
              >
                {(removeVideoAnalyse, data) => (
                  <Button
                    disabled={data.loading}
                    danger
                    small
                    right
                    onClick={() =>
                      deleteVideoAnalyse(removeVideoAnalyse, videoAnalyse)
                    }
                  >
                    <Flex alignItems="center" width={1}>
                      <Icon
                        id="delete"
                        fillColor={props => props.theme.colors.danger}
                      />
                    </Flex>
                  </Button>
                )}
              </Mutation>
            )}
          </Flex>
        )}
      </Box>

      <List width={[1, 1, 1 / 2]} pl={[0, 0, 2]}>
        <H3 upper lighter underlined>
          <FormattedMessage {...messages.analiseOverviewAnalyseTitle} />
        </H3>

        {videoAnalyse.analyses.map(analyse => (
          <ListItem
            width={1}
            py={3}
            key={analyse.id}
            alignItems="center"
            metaData={`${createFormattedDate(analyse.date_created)}`}
            content={`${formatBuildingBlocks(analyse)}`}
          >
            <Flex width={[1, 3 / 7]} justifyContent="flex-end">
              <Box width="auto">
                <Button
                  primary
                  small
                  onClick={() =>
                    push(
                      `${ROUTE_ANALYSE_VIDEO}/${videoAnalyse.id}/${analyse.id}`
                    )
                  }
                >
                  <Flex alignItems="center">
                    <Icon
                      id="edit"
                      fillColor={props => props.theme.colors.white}
                    />
                  </Flex>
                </Button>
              </Box>
              <Box width="auto" ml={2}>
                <Mutation
                  mutation={MUTATION_REMOVE_ANALYSE}
                  refetchQueries={[
                    {
                      query: QUERY_GET_VIDEO_ANALYSES,
                      variables: {
                        personId: isImpersonator ? isImpersonator.id : user.id
                      }
                    }
                  ]}
                >
                  {(removeAnalyse, data) => (
                    <Button
                      small
                      danger
                      disabled={data.loading}
                      onClick={() => deleteAnalyse(removeAnalyse, analyse)}
                    >
                      <Flex alignItems="center">
                        <Icon
                          id="delete"
                          fillColor={props => props.theme.colors.danger}
                        />
                      </Flex>
                    </Button>
                  )}
                </Mutation>
              </Box>
            </Flex>
          </ListItem>
        ))}

        {videoAnalyse.analyses.length === 0 && (
          <ListItem
            width={1}
            py={3}
            alignItems="center"
            content={`${intl.formatMessage(messages.analiseNoAnalyses)}`}
          />
        )}
      </List>

      {shareAble && (
        <List width={[1, 1, 1 / 2]} pl={[0, 0, 2]}>
          <Flex flexWrap="wrap">
            <Flex width={[1, 5 / 7]}>
              <H3 upper lighter underlined>
                <FormattedMessage {...messages.analyseSharedTitle} />
              </H3>
            </Flex>
          </Flex>

          <ListItem width={1} py={3} alignItems="center">
            <Mutation
              mutation={MUTATION_SHARE_ANALYSE}
              refetchQueries={[
                {
                  query: QUERY_GET_VIDEO_ANALYSES,
                  variables: {
                    personId: isImpersonator ? isImpersonator.id : user.id
                  }
                }
              ]}
            >
              {(shareAnalyse, data) => (
                <Fragment>
                  <Flex width={[1, 5 / 7]} alignItems="center">
                    <Input
                      id="shareToEmailInput"
                      value={email}
                      placeholder="Email..."
                      onChange={onUpdateEmail}
                    />
                  </Flex>
                  <Flex width={[1, 2 / 7]} justifyContent="flex-end">
                    <Button
                      primary
                      $onTitleWithIcon
                      disabled={data.loading || !email}
                      onClick={() => onShareAnalyse(shareAnalyse)}
                    >
                      <span>Delen</span>
                      <Icon
                        id="plus"
                        fillColor={props => props.theme.colors.white}
                      />
                    </Button>
                  </Flex>
                </Fragment>
              )}
            </Mutation>
          </ListItem>

          {videoAnalyse.sharedWith &&
            videoAnalyse.sharedWith.map(person => (
              <ListItem
                key={person.id}
                width={1}
                py={3}
                alignItems="center"
                content={`${person.firstname} ${person.lastname}`}
              >
                <Flex width={[1, 3 / 7]} justifyContent="flex-end">
                  <Box width="auto" ml={2}>
                    <Mutation
                      mutation={MUTATION_UNSHARE_ANALYSE}
                      refetchQueries={[
                        {
                          query: QUERY_GET_VIDEO_ANALYSES,
                          variables: {
                            personId: isImpersonator
                              ? isImpersonator.id
                              : user.id
                          }
                        }
                      ]}
                    >
                      {(unshareAnalyse, data) => (
                        <Button
                          small
                          danger
                          disabled={data.loading}
                          onClick={() =>
                            onUnshareAnalyse(unshareAnalyse, person)
                          }
                        >
                          <Flex alignItems="center">
                            <Icon
                              id="delete"
                              fillColor={props => props.theme.colors.danger}
                            />
                          </Flex>
                        </Button>
                      )}
                    </Mutation>
                  </Box>
                </Flex>
              </ListItem>
            ))}

          {videoAnalyse.sharedWith.length === 0 && (
            <ListItem
              width={1}
              py={3}
              alignItems="center"
              content={`${intl.formatMessage(messages.analyseSharedNoResult)}`}
            />
          )}
        </List>
      )}
    </Flex>
  );
}

export default observer(VideoAnalyseView);
